import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Loader2, DollarSign, AlertTriangle, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Month names for display
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function PricingManager() {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [overlaps, setOverlaps] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [formData, setFormData] = useState({
    season_name: '',
    start_date: '',
    end_date: '',
    price: 0,
    order: 0,
  });

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/pricing`);
      setPricing(res.data);
      // Check for overlaps
      checkOverlaps();
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const checkOverlaps = async () => {
    try {
      const res = await axios.post(`${API}/pricing/check-overlaps`);
      setOverlaps(res.data.overlaps || []);
    } catch (error) {
      console.error('Failed to check overlaps:', error);
    }
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Not set';
    try {
      const [startMonth, startDay] = startDate.split('-').map(Number);
      const [endMonth, endDay] = endDate.split('-').map(Number);
      return `${MONTHS[startMonth - 1]} ${startDay} - ${MONTHS[endMonth - 1]} ${endDay}`;
    } catch {
      return `${startDate} - ${endDate}`;
    }
  };

  const handleOpenDialog = (price = null) => {
    setValidationError(null);
    if (price) {
      setEditingPricing(price);
      setFormData({
        season_name: price.season_name,
        start_date: price.start_date || '',
        end_date: price.end_date || '',
        price: price.price,
        order: price.order || 0,
      });
    } else {
      setEditingPricing(null);
      setFormData({
        season_name: '',
        start_date: '',
        end_date: '',
        price: 0,
        order: pricing.length,
      });
    }
    setDialogOpen(true);
  };

  const validateDates = async () => {
    if (!formData.start_date || !formData.end_date) {
      setValidationError('Please enter both start and end dates');
      return false;
    }

    // Validate format MM-DD
    const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    if (!dateRegex.test(formData.start_date) || !dateRegex.test(formData.end_date)) {
      setValidationError('Date format must be MM-DD (e.g., 05-18 for May 18)');
      return false;
    }

    // Check for overlaps with other seasons
    try {
      const res = await axios.post(`${API}/pricing/validate`, formData, {
        params: { exclude_id: editingPricing?.id }
      });
      
      if (!res.data.is_valid) {
        const overlapList = res.data.overlaps.map(o => `${o.season} (${o.range})`).join(', ');
        setValidationError(`Date range overlaps with: ${overlapList}`);
        return false;
      }
    } catch (error) {
      console.error('Validation error:', error);
    }

    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.season_name || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate dates
    const isValid = await validateDates();
    if (!isValid) return;

    setSubmitting(true);
    try {
      if (editingPricing) {
        await axios.put(`${API}/pricing/${editingPricing.id}`, formData);
        toast.success('Pricing updated successfully');
      } else {
        await axios.post(`${API}/pricing`, formData);
        toast.success('Pricing added successfully');
      }
      setDialogOpen(false);
      fetchPricing();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to save pricing';
      toast.error(detail);
      setValidationError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pricing?')) return;
    
    try {
      await axios.delete(`${API}/pricing/${id}`);
      toast.success('Pricing deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete pricing');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Check if a season has overlap issues
  const hasOverlapIssue = (seasonName) => {
    return overlaps.some(o => o.season1 === seasonName || o.season2 === seasonName);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">Pricing Manager</h1>
          <p className="text-[#66605B]">Manage seasonal rental rates with strict date ranges</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
          data-testid="add-pricing-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Season
        </Button>
      </div>

      {/* Overlap Warning */}
      {overlaps.length > 0 && (
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Date Overlap Detected!</strong> The following seasons have overlapping dates:
            <ul className="mt-2 list-disc list-inside">
              {overlaps.map((o, idx) => (
                <li key={idx}>
                  <strong>{o.season1}</strong> ({o.range1}) overlaps with <strong>{o.season2}</strong> ({o.range2})
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* No Overlaps Success */}
      {overlaps.length === 0 && pricing.length > 1 && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            All pricing seasons have valid, non-overlapping date ranges.
          </AlertDescription>
        </Alert>
      )}

      {/* Pricing Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
        </div>
      ) : pricing.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pricing.map((season) => (
            <div
              key={season.id}
              className={`bg-white rounded-sm shadow-sm p-6 ${hasOverlapIssue(season.season_name) ? 'ring-2 ring-red-400' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-lg text-[#1C1C1C]">
                      {season.season_name}
                    </h3>
                    {hasOverlapIssue(season.season_name) && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Overlap
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-sm text-[#66605B]">
                    <Calendar className="w-3 h-3" />
                    {formatDateRange(season.start_date, season.end_date)}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenDialog(season)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(season.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-serif text-3xl text-[#1B4D3E]">
                  {formatPrice(season.price)}
                </span>
                <span className="text-sm text-[#66605B]">/ week</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-sm shadow-sm p-12 text-center">
          <DollarSign className="w-12 h-12 mx-auto mb-4 text-[#66605B] opacity-30" />
          <p className="text-[#66605B]">No pricing added yet</p>
          <Button
            onClick={() => handleOpenDialog()}
            variant="outline"
            className="mt-4"
          >
            Add First Season
          </Button>
        </div>
      )}

      {/* Date Range Reference */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-semibold text-amber-800 mb-2">📅 Date Format Guide</h3>
        <p className="text-sm text-amber-700 mb-2">
          Use <strong>MM-DD</strong> format (e.g., <code className="bg-amber-100 px-1 rounded">05-18</code> for May 18)
        </p>
        <div className="text-sm text-amber-700 grid grid-cols-2 md:grid-cols-4 gap-2">
          <span>Jan = 01</span>
          <span>Apr = 04</span>
          <span>Jul = 07</span>
          <span>Oct = 10</span>
          <span>Feb = 02</span>
          <span>May = 05</span>
          <span>Aug = 08</span>
          <span>Nov = 11</span>
          <span>Mar = 03</span>
          <span>Jun = 06</span>
          <span>Sep = 09</span>
          <span>Dec = 12</span>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingPricing ? 'Edit Pricing' : 'Add New Season'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {validationError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="season_name">Season Name *</Label>
              <Input
                id="season_name"
                value={formData.season_name}
                onChange={(e) => setFormData(prev => ({ ...prev, season_name: e.target.value }))}
                placeholder="e.g., Peak Summer"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date (MM-DD) *</Label>
                <Input
                  id="start_date"
                  value={formData.start_date}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, start_date: e.target.value }));
                    setValidationError(null);
                  }}
                  placeholder="e.g., 05-18"
                  required
                />
                <p className="text-xs text-gray-500">e.g., 07-11 for July 11</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date (MM-DD) *</Label>
                <Input
                  id="end_date"
                  value={formData.end_date}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, end_date: e.target.value }));
                    setValidationError(null);
                  }}
                  placeholder="e.g., 08-31"
                  required
                />
                <p className="text-xs text-gray-500">e.g., 08-31 for Aug 31</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Weekly Rate (CAD) *</Label>
              <Input
                id="price"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                min="0"
                step="100"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                min="0"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingPricing ? 'Update' : 'Add'} Pricing
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
